import React from 'react';
import { Dropdown } from '@insurancemenu/imenu-components-lib';
import { ActionUiPresenter, DocumentAction } from 'types/documentsService';
import {
  Archive,
  CheckCircle,
  ChevronDown,
  Delete,
  DoubleArrowRight,
  Download,
  Edit,
  FileSignature,
  NavigationClose,
  Notification,
  Description,
  UserPen,
} from '@insurancemenu/imenu-components-lib/dist/icons';
import { DropdownOptionType, OptionsAlignmentTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { IMBaseColors } from '@insurancemenu/imenu-components-lib/dist/utils/colors';
import { observer } from 'mobx-react-lite';
import { MissclikableAction, WarningModals } from 'store/modules/upload-documents/documentDelegate';
import ModalDelegate from 'store/general/modalDelegate';
import {
  ActionOptionTextStyled,
  DisabledOptionStyled,
  DisabledOptionTextStyled,
  DownloadButtonStyled,
  DropdownButtonStyled,
  DropdownOpenedButtonStyled,
} from './styled';
import { AVAILABLE_ACTIONS_DROPDOWN_TEST_ID, DOWNLOAD_DOCUMENT_BUTTON_TEST_ID } from '../constants';

interface AvailableActionsProps {
  availableActions: ActionUiPresenter[];
  warningModals: WarningModals;
  disableMutativeActions: boolean;
  isLoading: boolean;
}

interface OptionIconProps {
  actionType: DocumentAction;
}

function getOptionColor(actionType: DocumentAction): IMBaseColors | undefined {
  switch (actionType) {
    case DocumentAction.REJECT:
    case DocumentAction.REMOVE_UPLOAD:
    case DocumentAction.DISCARD:
      return 'red';
    case DocumentAction.APPROVE:
      return 'green';

    // Should not redefine default blue-black style for dropdown options
    default:
      return undefined;
  }
}

const OptionIcon: React.FC<OptionIconProps> = ({ actionType }) => {
  switch (actionType) {
    case DocumentAction.BYPASS:
      return <DoubleArrowRight width="2.4rem" />;
    case DocumentAction.REMOVE_UPLOAD:
      return <Delete width="2.4rem" />;
    case DocumentAction.DISCARD:
      return <Archive width="2.4rem" />;
    case DocumentAction.DOWNLOAD:
      return <Download width="2.4rem" />;
    case DocumentAction.REQUEST_FOR_SIGNATURE:
      return <FileSignature width="2.4rem" />;
    case DocumentAction.SEND_SIGN_REMINDER:
      return <Notification width="2.4rem" />;
    case DocumentAction.CHANGE_SIGNATURE_EMAIL:
      return <UserPen width="2.4rem" viewBox="-3 -3 27 27" />;
    case DocumentAction.EDIT:
      return <Edit width="2.4rem" />;
    case DocumentAction.EDIT_TEMPLATE:
      return <Description width="2.4rem" />;
    case DocumentAction.APPROVE:
      return <CheckCircle width="2.4rem" />;
    case DocumentAction.REJECT:
      // We should not viewBox prop normally, all the icons must be of the same size,
      // if this happens another time, consider creating a new icon.
      return <NavigationClose width="2.4rem" viewBox="3 3 25 25" />;

    default:
      return null;
  }
};

function mapActionToDropdownOption(action: ActionUiPresenter, warningModal?: ModalDelegate): DropdownOptionType {
  const handleAction = warningModal?.open || action.handler;
  return {
    value: action.type,
    label: (
      <ActionOptionTextStyled $actionType={action.type}>
        <OptionIcon actionType={action.type} />
        {action.name}
      </ActionOptionTextStyled>
    ),
    'data-test-id': `${action.type}-action-test-id`,
    customColor: !action.isDisabled ? getOptionColor(action.type) : undefined,
    // This logic adds the 'disabled' view option instead of complete removal.
    onOptionClick: !action.isDisabled ? handleAction : undefined,
    ...(action.isDisabled && {
      renderFunc: () => (
        <DisabledOptionStyled
          key={action.type}
          data-test-id={`${action.type}-action-test-id`}
          data-role="disabled-option"
        >
          <DisabledOptionTextStyled>
            <OptionIcon actionType={action.type} />
            {action.name}
          </DisabledOptionTextStyled>
        </DisabledOptionStyled>
      ),
    }),
  };
}

export const AvailableActions: React.FC<AvailableActionsProps> = observer(
  ({ availableActions, disableMutativeActions, warningModals, isLoading }) => {
    if (disableMutativeActions) {
      const downloadAction = availableActions.find((a) => a.type === DocumentAction.DOWNLOAD);
      if (!downloadAction) {
        return null;
      }
      return (
        <DownloadButtonStyled
          type="text"
          color="primary"
          onClick={downloadAction.handler}
          leftIconProp={<Download />}
          data-test-id={DOWNLOAD_DOCUMENT_BUTTON_TEST_ID}
        >
          Download Document
        </DownloadButtonStyled>
      );
    }

    return (
      <Dropdown
        name="More Options"
        options={availableActions.map((action) =>
          mapActionToDropdownOption(action, warningModals[action.type as unknown as MissclikableAction]),
        )}
        closedElement={
          <DropdownButtonStyled
            type="text"
            color="primary"
            rightIconProp={<ChevronDown />}
            disabled={isLoading || availableActions.length < 1}
          >
            More Options
          </DropdownButtonStyled>
        }
        openedElement={
          <DropdownOpenedButtonStyled color="primary" rightIconProp={<ChevronDown />}>
            More Options
          </DropdownOpenedButtonStyled>
        }
        optionsWidth="23rem"
        optionsAlignment={OptionsAlignmentTypes.BOTTOM_LEFT}
        data-test-id={AVAILABLE_ACTIONS_DROPDOWN_TEST_ID}
      />
    );
  },
);
